import { IS_DEV, RD_BASE, SKILL_KEY } from './config';

export async function rdSkill(endpoint, body = {}) {
  // ser_inv 走 proxy 共享缓存（30min TTL + 并发去重 + RootData 抖动时 SWR 兜底）
  // autocomplete + fetchFullProject + resolveProjectIdentity 共享同一缓存，去重收益最大
  if (endpoint === 'ser_inv' && body?.query) {
    return rdSerInvCached(body.query, !!body.precise_x_search);
  }

  // 本地开发走 package.json proxy → api.rootdata.com
  // 生产环境走 Railway 代理
  const url = IS_DEV
    ? `/open/skill/${endpoint}`
    : `${RD_BASE}/open/skill/${endpoint}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);

  let r;
  try {
    r = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${SKILL_KEY}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === "AbortError") throw new Error(`RootData ${endpoint} timeout (15s)`);
    throw e;
  }
  clearTimeout(timer);

  const text = await r.text();
  let j;
  try {
    j = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`RootData ${endpoint} HTTP ${r.status}: ${text.slice(0, 120) || r.statusText}`);
  }

  if (!r.ok || j.result !== 200) {
    const status = j.result || r.status;
    const message = j.message || j.error || r.statusText || "request failed";
    throw new Error(`RootData ${endpoint} error ${status}: ${message}`);
  }
  return j.data;
}

// ── ser_inv 命中 proxy 共享缓存 ────────────────────────────────────────────
// 失败时回退到直连 RootData（保留旧行为，避免 proxy 故障让搜索整体失效）
async function rdSerInvCached(query, preciseX) {
  const base = IS_DEV ? '' : RD_BASE;
  const qs = new URLSearchParams({ q: query });
  if (preciseX) qs.set('precise', '1');
  try {
    const res = await fetch(`${base}/api/ser_inv?${qs}`);
    if (res.ok) {
      const j = await res.json();
      return j.data;
    }
  } catch {
    // proxy 失败 → fall through 走直连
  }
  // Fallback: 直连 RootData，保持现有错误语义
  return rdSkillDirect('ser_inv', { query, precise_x_search: preciseX });
}

// 直连 RootData 的原始实现（仅 fallback 用）
async function rdSkillDirect(endpoint, body) {
  const url = IS_DEV
    ? `/open/skill/${endpoint}`
    : `${RD_BASE}/open/skill/${endpoint}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  let r;
  try {
    r = await fetch(url, {
      method: "POST",
      headers: { "Authorization": `Bearer ${SKILL_KEY}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e.name === "AbortError") throw new Error(`RootData ${endpoint} timeout (15s)`);
    throw e;
  }
  clearTimeout(timer);
  const text = await r.text();
  let j;
  try { j = text ? JSON.parse(text) : {}; }
  catch { throw new Error(`RootData ${endpoint} HTTP ${r.status}: ${text.slice(0, 120) || r.statusText}`); }
  if (!r.ok || j.result !== 200) {
    const status = j.result || r.status;
    const message = j.message || j.error || r.statusText || "request failed";
    throw new Error(`RootData ${endpoint} error ${status}: ${message}`);
  }
  return j.data;
}

export async function fetchPipeline(minAmount = 1000000) {
  const now = new Date();
  const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const start_time = `${monthAgo.getFullYear()}-${String(monthAgo.getMonth() + 1).padStart(2, "0")}`;
  return rdSkill("get_fac", { page: 1, page_size: 50, min_amount: minAmount, start_time });
}

export async function fetchTrending(days = 1) {
  // 优先走服务端列表缓存（warm-trending cron 每日预热，命中率 >95%，< 50ms）
  // 缓存冷启动时自动回填，仍比直连 RootData API 快（无额外请求）
  try {
    const res = await fetch(`${IS_DEV ? '' : (process.env.REACT_APP_PROXY || '')}/api/trending?days=${days}`);
    if (res.ok) {
      const json = await res.json();
      const list = json.data?.data || json.data || [];
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch { /* 降级到直连 */ }
  // 回退：直连 RootData API
  return rdSkill("hot_index", { days });
}
